import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api } from './api'

type User = {
  id: string
  username: string
  displayName: string
  roles: string[]
  avatarUrl?: string | null
  profileComplete?: boolean
}

type Profile = {
  userId: string
  bio: string | null
  avatarUrl: string | null
  setupComplete?: boolean
  updatedAt?: string
}

type Wallet = { balance: number; holds: number; available: number; currency: string }

type AuthState = {
  user: User | null
  profile: Profile | null
  token: string | null
  wallet: Wallet | null
  loading: boolean
  profileComplete: boolean
  /** Name-only friends entry (create or return). */
  enter: (name: string) => Promise<void>
  login: (username: string, password?: string) => Promise<void>
  register: (input: {
    username: string
    password: string
    inviteCode?: string
    displayName?: string
  }) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('usf_token'))
  const [wallet, setWallet] = useState<Wallet | null>(null)
  const [loading, setLoading] = useState(true)

  const applySession = (payload: {
    user: User
    profile?: Profile | null
    wallet?: Wallet | null
    token: string
  }) => {
    localStorage.setItem('usf_token', payload.token)
    setToken(payload.token)
    setUser(payload.user)
    if (payload.profile !== undefined) setProfile(payload.profile)
    if (payload.wallet !== undefined) setWallet(payload.wallet)
    setLoading(false)
  }

  const refresh = async () => {
    const t = token || localStorage.getItem('usf_token')
    if (!t) {
      setUser(null)
      setProfile(null)
      setWallet(null)
      setLoading(false)
      return
    }
    try {
      const me = await api<{
        user: User
        profile: Profile | null
        wallet: Wallet
      }>('/api/me', { token: t })
      setUser(me.user)
      setProfile(me.profile)
      setWallet(me.wallet)
    } catch (e: any) {
      const status = e?.status
      // Only clear session on auth failure — keep register/login payload on network blips
      if (status === 401 || status === 403) {
        localStorage.removeItem('usf_token')
        setToken(null)
        setUser(null)
        setProfile(null)
        setWallet(null)
      }
      // otherwise leave existing user/wallet from applySession
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const profileComplete = Boolean(
    user?.profileComplete ??
      profile?.setupComplete ??
      (user?.displayName && user.displayName.trim().length >= 2)
  )

  const value = useMemo<AuthState>(
    () => ({
      user,
      profile,
      token,
      wallet,
      loading,
      profileComplete,
      async enter(name) {
        const res = await api<{
          user: User
          profile?: Profile | null
          wallet?: Wallet
          token: string
        }>('/api/auth/enter', {
          method: 'POST',
          body: { name: name.trim() },
        })
        applySession(res)
      },
      async login(username, password) {
        const res = await api<{
          user: User
          profile?: Profile | null
          wallet?: Wallet
          token: string
        }>('/api/auth/login', {
          method: 'POST',
          body: password ? { username, password } : { username },
        })
        applySession(res)
      },
      async register(input) {
        const res = await api<{
          user: User
          profile?: Profile | null
          wallet?: Wallet
          token: string
        }>('/api/auth/register', {
          method: 'POST',
          body: {
            username: input.username.trim().toLowerCase(),
            password: input.password,
            inviteCode: input.inviteCode || 'FRIENDS2026',
            displayName: input.displayName || input.username.trim(),
          },
        })
        applySession(res)
      },
      async logout() {
        try {
          await api('/api/auth/logout', { method: 'POST', token })
        } finally {
          localStorage.removeItem('usf_token')
          setToken(null)
          setUser(null)
          setProfile(null)
          setWallet(null)
        }
      },
      refresh,
    }),
    [user, profile, token, wallet, loading, profileComplete]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside provider')
  return ctx
}
